import { CalendarHeart, ListChecks, FileSignature, MapPinned, PartyPopper, Smartphone, ArrowRight, MessageCircle, Gift, Sparkles } from "lucide-react";
import { LINE_URL } from "../constants";

const STEPS = [
  { n: 1, day: "Day 1", icon: CalendarHeart, title: "預約顧問諮詢", desc: "先聊聊就好，不用準備任何資料。我們先聽您的現況，跟您想達成的目標。", get: "現況診斷 ＋ 專屬顧問 LINE", bg: "#FFF7ED", color: "#FF6B00" },
  { n: 2, day: "Day 1", icon: ListChecks, title: "選擇合適的解決方案", desc: "依營收規模與痛點，顧問陪您挑出最划算的那一個 — 不推您用不到的。", get: "專屬報價單 PDF", bg: "#F0FDFA", color: "#0D9488" },
  { n: 3, day: "Day 2–3", icon: FileSignature, title: "線上合約確認 × 目標規劃", desc: "線上確認合約內容，一起訂出第一季的營收目標與行銷節奏。", get: "第一季目標表 ＋ 行銷節奏表", bg: "#EFF6FF", color: "#0284C7" },
  { n: 4, day: "Day 4–7", icon: MapPinned, title: "預約現場勘查", desc: "團隊親自走一趟現場，確認場域能做到我們承諾的成效，順便看拍攝角度。", get: "現場評估 ＋ 拍攝場景建議", bg: "#FEF9C3", color: "#B8860B" },
];

const StepCard = ({ s }) => {
  const Icon = s.icon;
  return (
    <div data-testid={`onboarding-step-${s.n}`} className="relative bg-white rounded-3xl p-6 md:p-7 shadow-md border border-slate-100 flex flex-col hover:-translate-y-1 transition-transform duration-300">
      <div className="flex items-center justify-between mb-5">
        <span className="text-xs font-black tracking-[0.2em] text-slate-400">EP.0{s.n}</span>
        <span className="text-xs md:text-sm font-black rounded-full px-3 py-1" style={{ background: s.bg, color: s.color }}>{s.day}</span>
      </div>
      <div className="w-16 h-16 md:w-[72px] md:h-[72px] rounded-[1.4rem] flex items-center justify-center mb-5 rotate-[-4deg]" style={{ background: s.bg, color: s.color }}>
        <Icon className="w-8 h-8 md:w-9 md:h-9" strokeWidth={2.2} />
      </div>
      <div className="text-xl md:text-2xl font-black text-[#0A2540] leading-tight mb-3">{s.title}</div>
      <p className="text-base text-[#475569] leading-relaxed mb-5 flex-1">{s.desc}</p>
      <div className="flex items-start gap-2 rounded-2xl px-3.5 py-2.5" style={{ background: s.bg }}>
        <Gift className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: s.color }} />
        <div className="text-sm leading-snug">
          <span className="font-bold" style={{ color: s.color }}>您會拿到：</span>
          <span className="font-semibold text-[#0A2540]">{s.get}</span>
        </div>
      </div>
    </div>
  );
};

const Rail = () => {
  const nodes = ["Day 1", "Day 1", "Day 2–3", "Day 4–7", "Day 7–10"];
  return (
    <div className="hidden lg:block mb-16" data-testid="onboarding-rail">
      <div className="relative h-2 rounded-full bg-slate-200 mx-[10%]">
        <div className="absolute inset-y-0 left-0 w-full rounded-full bg-gradient-to-r from-[#FF6B00] via-[#0D9488] to-[#F5C24A]"></div>
        {nodes.map((d, i) => (
          <div key={i} className="absolute -top-2.5 flex flex-col items-center" style={{ left: `${(i / (nodes.length - 1)) * 100}%`, transform: "translateX(-50%)" }}>
            <div className={`rounded-full border-4 border-white shadow ${i === nodes.length - 1 ? "w-8 h-8 bg-[#F5C24A] -mt-1" : "w-7 h-7 bg-[#0A2540]"}`}></div>
            <div className={`mt-2 whitespace-nowrap text-xs font-black ${i === nodes.length - 1 ? "text-[#B8860B]" : "text-[#0A2540]"}`}>{d}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const OnboardingFlowSection = () => (
  <section id="onboarding" data-testid="onboarding-section" className="py-14 md:py-28 bg-[#FAFAF7] relative overflow-hidden">
    <div className="absolute -top-24 -left-24 w-72 h-72 bg-[#FF6B00]/5 rounded-full blur-3xl"></div>
    <div className="absolute -bottom-24 -right-24 w-80 h-80 bg-[#0D9488]/5 rounded-full blur-3xl"></div>
    <div className="max-w-7xl mx-auto px-5 md:px-12 relative">
      <div className="text-center mb-10 md:mb-14">
        <div className="inline-flex items-center gap-2 bg-[#0A2540] text-white px-5 py-2.5 rounded-full text-sm md:text-base font-bold mb-6">
          <Sparkles className="w-4 h-4 text-[#F5C24A]" />
          接下來會發生什麼？
        </div>
        <h2 className="text-3xl sm:text-5xl font-black text-[#0A2540] tracking-tight mb-4 leading-tight">
          從第一次聊天到正式開始，<br className="hidden sm:block" />
          只要 <span className="text-[#FF6B00]">7–10 天</span>
        </h2>
        <p className="text-lg md:text-xl text-[#475569] max-w-3xl mx-auto leading-relaxed">
          五集式的啟動旅程，每一集我們都會牽著您走，不用自己摸索。<br className="hidden md:block" />
          <span className="font-bold text-[#0A2540]">第 10 天，您的行銷部門就開始替您上班。</span>
        </p>
      </div>

      <Rail />

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-8 md:mb-10">
        {STEPS.map((s) => <StepCard key={s.n} s={s} />)}
      </div>

      {/* 最終集：分岔結果 */}
      <div className="relative bg-white rounded-[2rem] p-5 md:p-8 shadow-md border border-slate-100">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <span className="text-xs font-black tracking-[0.2em] text-slate-400">EP.05 · 最終集</span>
            <span className="text-xs md:text-sm font-black rounded-full px-3 py-1 bg-[#FEF9C3] text-[#B8860B]">Day 7–10</span>
          </div>
          <div className="text-xl md:text-2xl font-black text-[#0A2540]">現場勘查後，兩種結果都幫您安排好了</div>
        </div>
        <div className="grid md:grid-cols-5 gap-4 md:gap-5 items-stretch">
          <div data-testid="onboarding-step-5a" className="md:col-span-3 relative bg-gradient-to-br from-[#0D9488] to-[#0F766E] rounded-3xl p-6 md:p-8 text-white shadow-xl overflow-hidden">
            <div className="absolute -top-10 -right-10 w-44 h-44 bg-white/10 rounded-full blur-2xl"></div>
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 bg-white/15 border border-white/30 rounded-full px-3 py-1 text-sm font-bold mb-4">
                <PartyPopper className="w-4 h-4 text-[#F5C24A]" /> 5-1 · 絕大多數業主走這條
              </div>
              <div className="text-2xl md:text-3xl font-black leading-tight mb-3">確認可以做到 → 匯款後正式開始</div>
              <p className="text-base md:text-lg text-white/85 leading-relaxed mb-5">
                啟動費與首月月費匯入當天，系統開通、社群與客服團隊進場 —
                <span className="font-black text-[#F5C24A]"> 您的行銷部門正式上班。</span>
              </p>
              <div className="flex flex-wrap gap-2 text-sm font-bold">
                {["PMS 當天開通", "社群建置", "客服團隊進場", "首批素材拍攝排程", "第一批 KOL 邀約"].map((t) => (
                  <span key={t} className="bg-white/15 rounded-full px-3.5 py-1.5">{t}</span>
                ))}
              </div>
            </div>
          </div>
          <div data-testid="onboarding-step-5b" className="md:col-span-2 bg-[#FAFAF7] border-2 border-dashed border-slate-300 rounded-3xl p-6 md:p-7 flex flex-col">
            <div className="inline-flex w-fit items-center gap-1.5 bg-white text-slate-500 rounded-full px-3 py-1 text-sm font-bold mb-4 border border-slate-200">
              <Smartphone className="w-4 h-4" /> 5-2 · 若現場暫時不適合
            </div>
            <div className="text-xl md:text-2xl font-black text-[#0A2540] leading-tight mb-3">先上架人客多包棟專屬 App</div>
            <p className="text-base text-[#475569] leading-relaxed">
              不勉強合作，但也不讓您白跑一趟 — 免費上架我們的包棟專屬 App，先接觸人客多會員，等場域準備好再一起衝。
            </p>
          </div>
        </div>
      </div>

      <div className="mt-10 md:mt-12 text-center">
        <a href={LINE_URL} target="_blank" rel="noopener noreferrer" data-testid="onboarding-cta"
          className="inline-flex items-center gap-2.5 bg-[#FF6B00] hover:bg-[#E65A00] text-white font-black rounded-full px-9 py-4 md:py-5 text-base md:text-lg shadow-lg shadow-orange-500/30 transition-colors">
          <MessageCircle className="w-5 h-5" />
          從 EP.01 開始：LINE 預約顧問聊聊
          <ArrowRight className="w-5 h-5" />
        </a>
        <div className="mt-3 text-sm text-[#475569]">今天預約，最快下週就能正式開始</div>
      </div>
    </div>
  </section>
);

export default OnboardingFlowSection;
